/**
 * Snowcubes Reviews Widget Components
 * - Stateless DOM/string builders extracted from reviews-widget.js.
 * - These are leaf utilities: no `this`, no instance state, no event wiring,
 *   so moving them out cannot affect custom-element define timing.
 * - Browser global: registers helpers on window.SnowcubesReviewsComponents.
 * - Load order: this file MUST load BEFORE reviews-widget.js (the custom
 *   element consumes these builders).
 */
(function initSnowcubesReviewsComponents(global) {
  'use strict';

  var doc = global.document;

  var TARGET_REVIEW_HASH_PREFIX = '#snowcubes-review-';
  var DEFAULT_TARGET_AUTOLOAD_PAGES = 5;

  function translate(key, values) {
    var table = global.SnowcubesLocale || {};
    var value = table[key];
    if (typeof value !== 'string' || !value) return key;
    return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, function (_match, name) {
      return values && values[name] !== undefined ? String(values[name]) : '';
    });
  }

  function createElement(tagName, className, text) {
    var node = doc.createElement(tagName);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function createStar(rating, index) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = doc.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add(index <= Math.round(rating) ? 'is-filled' : 'is-empty');

    var path = doc.createElementNS(ns, 'path');
    path.setAttribute(
      'd',
      'M10 1.7 12.5 7l5.8.7-4.3 4 1.1 5.7L10 14.5 4.9 17.4 6 11.7 1.7 7l5.8-.7L10 1.7z',
    );
    svg.appendChild(path);
    return svg;
  }

  function createStars(rating, translateFn) {
    var rounded = Math.max(0, Math.min(5, Number(rating) || 0));
    var label = (translateFn || translate)('pages.reviews.stars_label', { rating: rounded });
    var wrapper = createElement('span', 'sc-reviews__stars');
    wrapper.setAttribute('role', 'img');
    wrapper.setAttribute('aria-label', label);
    for (var index = 1; index <= 5; index += 1) {
      wrapper.appendChild(createStar(rounded, index));
    }
    return wrapper;
  }

  function formatDate(value, locale) {
    if (!value) return '';
    try {
      return new Intl.DateTimeFormat(locale || 'en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        // Pin to store-local time so a UTC ISO date near midnight doesn't render
        // as the previous calendar day for US viewers (west of UTC).
        timeZone: 'America/New_York',
      }).format(new Date(value));
    } catch (_) {
      return '';
    }
  }

  function safeReviewAnchorId(id) {
    var value = String(id || '')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return value ? 'snowcubes-review-' + value : '';
  }

  function getTargetReviewIdFromLocation(locationLike) {
    var hash = locationLike && locationLike.hash ? String(locationLike.hash) : '';
    if (hash.indexOf(TARGET_REVIEW_HASH_PREFIX) !== 0) return '';
    return hash
      .slice(TARGET_REVIEW_HASH_PREFIX.length)
      .replace(/[^a-zA-Z0-9_-]+/g, '')
      .trim();
  }

  function getTargetAutoloadMax(node) {
    var value = Number(node && node.getAttribute('data-target-autoload-pages'));
    if (!Number.isFinite(value) || value < 0) return DEFAULT_TARGET_AUTOLOAD_PAGES;
    return Math.min(Math.floor(value), 20);
  }

  var api = {
    TARGET_REVIEW_HASH_PREFIX: TARGET_REVIEW_HASH_PREFIX,
    DEFAULT_TARGET_AUTOLOAD_PAGES: DEFAULT_TARGET_AUTOLOAD_PAGES,
    translate: translate,
    createElement: createElement,
    createStar: createStar,
    createStars: createStars,
    formatDate: formatDate,
    safeReviewAnchorId: safeReviewAnchorId,
    getTargetReviewIdFromLocation: getTargetReviewIdFromLocation,
    getTargetAutoloadMax: getTargetAutoloadMax,
  };

  global.SnowcubesReviewsComponents = global.SnowcubesReviewsComponents || {};
  Object.assign(global.SnowcubesReviewsComponents, api);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
